import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Box, Button, Checkbox, Group, Loader, Modal, Pagination, Paper, Progress,
  ScrollArea, SegmentedControl, Select, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconBolt, IconCircleCheck, IconCircleX, IconAlertTriangle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../../hooks/useAuthenticatedPost';
import { useWorkspaceScope } from '../../../workspace/WorkspaceScopeContext';
import config from '../../../config';
import DraftBadge from '../../common/DraftBadge';

const DATE_PRESETS = { 'Any time': null, 'Today': 0, 'Last 3 days': 3, 'Last 7 days': 7, 'Last 30 days': 30 };
// The lower bound of a date preset ('Today', 'Last 7 days', …), as an ARIZONA wall-clock timestamp.
//
// The DB stores rfq_create_date in Arizona local time (UTC-7, no DST). This used to return
// `d.toISOString()` — which converts the local midnight to UTC, adding 7 hours in Arizona — so the backend
// compared a 07:00 bound against Arizona-local dates and "Today" silently meant ">= 7 AM today". It went
// unnoticed until a 955-deal day pushed the 07:00-onward count to exactly the 400-row page cap, cutting the
// list off right at 7 AM.
//
// Fix: anchor the date to America/Phoenix and emit a local-naive string (no 'Z'), so the bound is Arizona
// midnight regardless of the viewer's own timezone, matching how the DB stores the column.
function windowFrom(days) {
  if (days == null) return null;
  // en-CA formats as YYYY-MM-DD; the timeZone option gives Arizona's calendar date, wherever the browser is.
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Phoenix' })
    .format(new Date()).split('-').map(Number);
  const az = new Date(y, m - 1, d);       // browser-local Date at Arizona's midnight-of-today
  az.setDate(az.getDate() - days);
  const p = (x) => String(x).padStart(2, '0');
  return `${az.getFullYear()}-${p(az.getMonth() + 1)}-${p(az.getDate())}T00:00:00`;
}
function presetOfFrom(from) {
  if (!from) return 'Any time';
  const match = Object.entries(DATE_PRESETS).find(([, days]) => days != null && windowFrom(days) === from);
  return match ? match[0] : 'Any time';
}

// The received-date range pickers speak the SAME local-naive format as windowFrom (no 'Z'): the backend
// compares against Arizona-local rfq_create_date, so a UTC-converted value would re-introduce the +7h shift
// this file just fixed. The user picks a wall-clock time; we send exactly that wall-clock time.
const pad2 = (x) => String(x).padStart(2, '0');
function toLocalNaive(dateOrNull) {
  if (!dateOrNull) return null;
  const d = dateOrNull instanceof Date ? dateOrNull : new Date(dateOrNull);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
       + `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
}
// A stored 'YYYY-MM-DDTHH:mm:ss' string (no 'Z') parses back as LOCAL time, round-tripping the picker cleanly.
function toDate(s) { return s ? new Date(s) : null; }

// Batch scope presets → request body fragment.
const BATCH_SCOPES = {
  '5 deals':        { count: 5 },
  '10 deals':       { count: 10 },
  '20 deals':       { count: 20 },
  '50 deals':       { count: 50 },
  'Last 24 hours':  { sinceDays: 1 },
  'Last 3 days':    { sinceDays: 3 },
};

const PRIORITY_COLORS = { Routine: 'gray', Expedite: 'yellow', AOG: 'red' };
const ENRICHMENT_COLORS = { current: 'green', stale: 'yellow', none: 'gray' };
const COVERAGE_COLORS = { have_all: 'green', have_some: 'yellow', have_none: 'red', unknown: 'gray' };

// SSP health for the deal. The grain here is the DEAL but SSP is a per-LINE fact, so show the counts:
// how many lines are priced & current, priced but expired, and have no governed price at all. Zero-count
// states are hidden so a clean deal reads as a single green tick rather than three numbers to parse.
function SspCounts({ o }) {
  const ok = Number(o.ssp_current) || 0;
  const stale = Number(o.ssp_stale) || 0;
  const missing = Number(o.ssp_missing) || 0;
  if (ok + stale + missing === 0) return <Text c="dimmed" size="xs">—</Text>;   // not priced yet (re-enrich)

  const parts = [
    { n: ok, color: 'green', icon: <IconCircleCheck size={14} />, what: 'priced, current' },
    { n: stale, color: 'yellow', icon: <IconAlertTriangle size={14} />, what: 'priced but EXPIRED' },
    { n: missing, color: 'red', icon: <IconCircleX size={14} />, what: 'no SSP on file' },
  ].filter((p) => p.n > 0);

  return (
    <Tooltip
      multiline w={230} withArrow
      label={parts.map((p) => `${p.n} line${p.n === 1 ? '' : 's'} ${p.what}`).join(' · ')}
    >
      <Group gap={4} wrap="nowrap" style={{ cursor: 'help' }}>
        {parts.map((p) => (
          <Badge key={p.color} size="sm" variant="light" color={p.color} leftSection={p.icon}>{p.n}</Badge>
        ))}
      </Group>
    </Tooltip>
  );
}
const ACTION_COLORS = {
  quote_now: 'green', quote_with_review: 'teal', hold_for_sourcing: 'yellow',
  // decided by a rule (customer on credit hold), never by a model — see EnrichmentRunner.DeterministicVerdict
  hold_for_credit: 'red',
  request_more_info: 'blue', deprioritize: 'gray',
};
const PAGE_SIZE = 50;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}
// DB datetimes are Arizona local — never TZ-convert. Drop any trailing Z/offset so the
// browser doesn't shift the value, then show date + H:M:S (24h).
function fmtReceivedAZ(iso) {
  if (!iso) return '—';
  const d = new Date(String(iso).replace(/(Z|[+-]\d{2}:?\d{2})$/, ''));
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour12: true, hour: 'numeric', minute: '2-digit', second: '2-digit' });
}
function isOverdue(iso) { return iso ? new Date(iso) < new Date() : false; }

export default function OpportunitiesWorklist() {
  const navigate = useNavigate();

  // Filter state lives in the shared, Vesper-owned WorkspaceScope (so it can be driven
  // externally later, e.g. by Nymeria). Behavior for a human user is unchanged.
  const { scope: ws, setWorklist } = useWorkspaceScope();
  const { enrichmentStatus, keyOnly, adjOnly, page, from, to, marketSegment, customer, sortField, sortDir } = ws.worklist;
  const setPage = (p) => setWorklist({ page: p });

  const url = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page - 1));               // API is 0-based
    p.set('pageSize', String(PAGE_SIZE));
    p.set('sortField', sortField || 'rfq_create_date');
    p.set('sortDir', sortDir || 'desc');
    if (enrichmentStatus !== 'all') p.set('enrichmentStatus', enrichmentStatus);
    if (keyOnly) p.set('isKeyCustomer', 'true');
    if (adjOnly) p.set('adjudicationNeeded', 'true');
    if (marketSegment) p.set('marketSegment', marketSegment);
    if (customer) p.set('customer', customer);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return `${config.apiBaseUrl}/api/vesper/worklist?${p.toString()}`;
  }, [page, enrichmentStatus, keyOnly, adjOnly, from, to, marketSegment, customer, sortField, sortDir]);

  const { data, isLoading, error, refetch } = useAuthenticatedData(url);

  // Market-segment slicer options.
  const { data: segments } = useAuthenticatedData(`${config.apiBaseUrl}/api/vesper/market-segments`);
  const segmentOptions = useMemo(
    () => (Array.isArray(segments) ? segments.map((s) => ({ value: s, label: s })) : []), [segments]);

  // Customer search — debounced into the shared scope so we don't refetch on every keystroke.
  const [custInput, setCustInput] = useState(customer || '');
  useEffect(() => {
    const h = setTimeout(() => {
      if ((custInput || '').trim() !== (customer || '')) setWorklist({ customer: custInput.trim() });
    }, 400);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custInput]);

  // Column sorting (click a header; re-click toggles direction).
  const toggleSort = (field) =>
    (sortField === field
      ? setWorklist({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' })
      : setWorklist({ sortField: field, sortDir: 'desc' }));
  const sortableTh = (field, label, right) => (
    <Table.Th style={{ cursor: 'pointer', whiteSpace: 'nowrap', textAlign: right ? 'right' : undefined }}
      onClick={() => toggleSort(field)}>
      {label}{sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </Table.Th>
  );

  // ── batch enrichment ──
  const { post } = useAuthenticatedPost();
  const [batchOpen, setBatchOpen] = useState(false);
  const [scope, setScope] = useState('5 deals');
  const [mode, setMode] = useState('pending');       // pending | all
  const [adjudicate, setAdjudicate] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [tick, setTick] = useState(0);
  const [batchMsg, setBatchMsg] = useState(null);
  const [starting, setStarting] = useState(false);

  const jobUrl = jobId ? `${config.apiBaseUrl}/api/vesper/enrich-batch/${jobId}?t=${tick}` : null;
  const { data: job } = useAuthenticatedData(jobUrl);

  useEffect(() => {
    if (!jobId) return undefined;
    if (job && job.running === false) { refetch?.(); return undefined; }
    const h = setInterval(() => setTick((t) => t + 1), 2000);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, job?.running]);

  const runBatch = async () => {
    setStarting(true); setBatchMsg(null);
    try {
      const res = await post(`${config.apiBaseUrl}/api/vesper/enrich-batch`,
        { ...BATCH_SCOPES[scope], mode, adjudicate });
      if (res?.jobId) { setJobId(res.jobId); setTick(0); }
      else setBatchMsg(res?.message || 'No matching deals to enrich.');
    } catch (e) {
      setBatchMsg(`Failed: ${e?.response?.data?.message || e.message}`);
    } finally { setStarting(false); }
  };

  const closeBatch = () => { setBatchOpen(false); setJobId(null); setBatchMsg(null); };

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack gap="sm" p="md">
      <DraftBadge />
      <Group justify="space-between" align="center">
        <Title order={2}>Opportunities</Title>
        <Text size="sm" c="dimmed">{total.toLocaleString()} opportunities</Text>
      </Group>

      <Group gap="md" align="center">
        <SegmentedControl
          size="xs"
          value={enrichmentStatus}
          onChange={(v) => setWorklist({ enrichmentStatus: v })}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Enriched', value: 'current' },
            { label: 'Stale', value: 'stale' },
            { label: 'Not enriched', value: 'none' },
          ]}
        />
        <Select
          size="xs" w={140} aria-label="Received"
          data={Object.keys(DATE_PRESETS)}
          value={presetOfFrom(from)}
          onChange={(v) => setWorklist({ from: windowFrom(DATE_PRESETS[v]), to: null })}
        />
        {/* Explicit received-date range, alongside the presets. Setting either overrides the preset (which
            then reads 'Any time'); leaving both empty falls back to the preset. `to` is the exclusive upper
            bound the backend already supports. */}
        <DateTimePicker
          size="xs" w={190} clearable valueFormat="MMM D, HH:mm" aria-label="Received from"
          placeholder="Received from"
          value={toDate(from)}
          onChange={(v) => setWorklist({ from: toLocalNaive(v) })}
        />
        <DateTimePicker
          size="xs" w={190} clearable valueFormat="MMM D, HH:mm" aria-label="Received to"
          placeholder="Received to"
          value={toDate(to)}
          onChange={(v) => setWorklist({ to: toLocalNaive(v) })}
        />
        <Select
          size="xs" w={180} placeholder="All segments" clearable searchable aria-label="Market segment"
          data={segmentOptions}
          value={marketSegment}
          onChange={(v) => setWorklist({ marketSegment: v || null })}
        />
        <TextInput
          size="xs" w={190} placeholder="Search customer…" aria-label="Search customer"
          value={custInput}
          onChange={(e) => setCustInput(e.currentTarget.value)}
        />
        <Checkbox label="Key customers" size="xs" checked={keyOnly}
          onChange={(e) => setWorklist({ keyOnly: e.currentTarget.checked })} />
        <Checkbox label="Needs review" size="xs" checked={adjOnly}
          onChange={(e) => setWorklist({ adjOnly: e.currentTarget.checked })} />
        <Button size="xs" variant="filled" ml="auto" onClick={() => navigate('/sales/cockpit')}>Open Cockpit</Button>
        <Button size="xs" variant="light" leftSection={<IconBolt size={14} />}
          onClick={() => setBatchOpen(true)}>Batch enrich</Button>
      </Group>

      <Modal opened={batchOpen} onClose={closeBatch} title="Batch enrich opportunities" size="md">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Runs the enrichment pipeline on a set of deals now (models: Stage&nbsp;3/4 nano·mini, adjudication terra).
          </Text>
          <Select label="How many" data={Object.keys(BATCH_SCOPES)} value={scope}
            onChange={(v) => setScope(v || '5 deals')} disabled={!!jobId} />
          <div>
            <Text size="xs" fw={600} mb={4}>Which deals</Text>
            <SegmentedControl size="xs" fullWidth value={mode} onChange={setMode} disabled={!!jobId}
              data={[{ label: 'Not enriched yet', value: 'pending' }, { label: 'Re-enrich all', value: 'all' }]} />
          </div>
          <Checkbox size="xs" label="Force adjudication (gpt-5.6-terra) on every deal"
            checked={adjudicate} onChange={(e) => setAdjudicate(e.currentTarget.checked)} disabled={!!jobId} />

          {batchMsg && <Text size="sm" c="red">{batchMsg}</Text>}

          {job && (
            <Paper withBorder radius="md" p="sm">
              <Group justify="space-between" mb={6}>
                <Text size="sm" fw={600}>{job.running ? 'Enriching…' : 'Done'}</Text>
                <Text size="sm" c="dimmed">{job.completed}/{job.total}</Text>
              </Group>
              <Progress value={job.total ? (job.completed / job.total) * 100 : 0} animated={job.running} />
              <Group gap="md" mt={6}>
                <Text size="xs" c="green">{job.succeeded} ok</Text>
                {job.failed > 0 && <Text size="xs" c="red">{job.failed} failed</Text>}
              </Group>
              {!job.running && job.results?.length > 0 && (
                <ScrollArea.Autosize mah={180} mt="xs">
                  <Table fz="xs">
                    <Table.Tbody>
                      {job.results.map((r) => (
                        <Table.Tr key={r.dealId}>
                          <Table.Td><Text size="xs" truncate maw={160}>{r.dealId}</Text></Table.Td>
                          <Table.Td><Badge size="xs" color={r.status === 'failed' ? 'red' : 'green'} variant="light">{r.status}</Badge></Table.Td>
                          <Table.Td ta="right">{r.priorityScore ?? '—'}</Table.Td>
                          <Table.Td>{r.recommendedAction || '—'}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea.Autosize>
              )}
            </Paper>
          )}

          <Group justify="flex-end">
            {!jobId && <Button size="xs" loading={starting} onClick={runBatch} leftSection={<IconBolt size={14} />}>Run</Button>}
            {jobId && !job?.running && <Button size="xs" variant="light" onClick={() => { setJobId(null); setBatchMsg(null); }}>Run another</Button>}
            <Button size="xs" variant="subtle" onClick={closeBatch}>{jobId && job?.running ? 'Close (keeps running)' : 'Close'}</Button>
          </Group>
        </Stack>
      </Modal>

      {isLoading && (
        <Stack align="center" mt="xl"><Loader /><Text c="dimmed">Loading opportunities…</Text></Stack>
      )}

      {error && (
        <Text c="red">Failed to load opportunities. Is the API reachable?</Text>
      )}

      {!isLoading && !error && (
        <Paper withBorder radius="md">
          <ScrollArea>
            <Table striped highlightOnHover fz="sm" miw={1100}>
              <Table.Thead>
                <Table.Tr>
                  {sortableTh('priority_score', 'Score')}
                  <Table.Th>Customer</Table.Th>
                  <Table.Th>Segment</Table.Th>
                  <Table.Th>Lines</Table.Th>
                  <Table.Th>Priority</Table.Th>
                  <Table.Th>Action</Table.Th>
                  <Table.Th>Coverage</Table.Th>
                  <Table.Th>Price</Table.Th>
                  <Table.Th>Due</Table.Th>
                  {sortableTh('rfq_create_date', 'Received')}
                  <Table.Th>Enriched</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((o) => (
                  <Table.Tr
                    key={o.deal_public_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/sales/cockpit?start=${encodeURIComponent(o.deal_public_id)}`)}
                  >
                    <Table.Td>
                      {o.priority_score != null ? (
                        <Badge color="indigo" variant="filled" size="sm">{o.priority_score}</Badge>
                      ) : <Text c="dimmed" size="xs">—</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Group gap={4}>
                          <Text size="sm" fw={500}>{o.buyer_company_name || o.customer_name_dw || o.buyer_contact_email || '—'}</Text>
                          {(o.IsAirframeKeyCustomer === 'Y' || o.IsEngineKeyCustomer === 'Y') &&
                            <Badge size="xs" color="grape" variant="light">Key</Badge>}
                        </Group>
                        {o.buyer_company_erp_key && <Text size="xs" c="dimmed">{o.buyer_company_erp_key}</Text>}
                      </Stack>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{o.MarketSegment || '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{o.line_count ?? '—'}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={PRIORITY_COLORS[o.priority] || 'gray'} variant="light" size="sm">
                        {o.is_mro ? 'MRO' : (o.priority || '—')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {o.recommended_action ? (
                        <Group gap={4}>
                          <Badge color={ACTION_COLORS[o.recommended_action] || 'gray'} variant="dot" size="sm">
                            {o.recommended_action.replace(/_/g, ' ')}
                          </Badge>
                          {o.adjudication_needed && (
                            <Tooltip label="Needs manager review">
                              <Badge color="orange" variant="light" size="xs">review</Badge>
                            </Tooltip>
                          )}
                        </Group>
                      ) : <Text c="dimmed" size="xs">—</Text>}
                    </Table.Td>
                    <Table.Td>
                      {o.inventory_coverage ? (
                        <Badge color={COVERAGE_COLORS[o.inventory_coverage] || 'gray'} variant="light" size="sm">
                          {o.inventory_coverage.replace(/_/g, ' ')}
                        </Badge>
                      ) : <Text c="dimmed" size="xs">—</Text>}
                    </Table.Td>
                    <Table.Td><SspCounts o={o} /></Table.Td>
                    <Table.Td>
                      <Text size="sm" c={isOverdue(o.rfq_response_due_by) ? 'red' : undefined}
                        fw={isOverdue(o.rfq_response_due_by) ? 600 : undefined}>
                        {fmtDate(o.rfq_response_due_by)}
                      </Text>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{fmtReceivedAZ(o.rfq_create_date)}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={ENRICHMENT_COLORS[o.enrichment_status] || 'gray'} variant="dot" size="sm">
                        {o.enrichment_status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {rows.length === 0 && (
                  <Table.Tr><Table.Td colSpan={10}>
                    <Text c="dimmed" ta="center" py="lg">No opportunities match these filters.</Text>
                  </Table.Td></Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      {totalPages > 1 && (
        <Box><Pagination value={page} onChange={setPage} total={totalPages} size="sm" /></Box>
      )}
    </Stack>
  );
}
